import { useSuspenseQuery, UseSuspenseQueryResult, useQuery, UseQueryResult, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import {
    prioritizedTasksResponseSchema,
    columnFiltersResponseSchema,
    responsibleUsersResponseSchema,
    PrioritizedTasksRequest,
    PrioritizedTasksResponse,
    ColumnFiltersRequest,
    ColumnFiltersResponse,
    ResponsibleUsersRequest,
    ResponsibleUsersResponse,
} from './todaysFocus.types';

export type { PrioritizedTasksRequest, PrioritizedTasksResponse, ColumnFiltersRequest, ColumnFiltersResponse, ResponsibleUsersRequest, ResponsibleUsersResponse } from './todaysFocus.types';
export type { PrioritizedTask, StatusOption, ResponsibleUser } from './todaysFocus.types';

// ---- Query keys ----

export const PRIORITIZED_TASKS_QUERY_KEY = 'TodaysFocusPrioritizedTasks';
export const COLUMN_FILTERS_QUERY_KEY = 'TodaysFocusColumnFilters';
export const RESPONSIBLE_USERS_QUERY_KEY = 'TodaysFocusResponsibleUsers';

// ---- Helper: build URLSearchParams from request (handles arrays as repeated keys) ----

const buildSearchParams = (request: PrioritizedTasksRequest): URLSearchParams => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(request)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
            for (const item of value) {
                params.append(key, String(item));
            }
        } else {
            params.set(key, String(value));
        }
    }
    return params;
};

// ---- Helper: deterministic serialization of a request for query keys (sorted arrays, sorted keys) ----
// Array field ORDER doesn't affect the request's meaning, but TanStack hashes query keys structurally
// (array order matters), so an unsorted key would refetch on every reorder of an unchanged filter set.

const sortArrayValue = <T>(value: T[]): T[] =>
    [...value].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

const serializeRequestKey = (request: Record<string, unknown>): string => {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(request).sort()) {
        const value = request[key];
        sorted[key] = Array.isArray(value) ? sortArrayValue(value) : value;
    }
    return JSON.stringify(sorted);
};

// ---- Prioritized Tasks ----

const loadPrioritizedTasks = async (request: PrioritizedTasksRequest): Promise<PrioritizedTasksResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}TodaysFocus/prioritized-tasks`, {
            searchParams: buildSearchParams(request),
        })
        .json();
    const parsed = prioritizedTasksResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadPrioritizedTasks: response did not match schema, using fallback');
        console.warn('Zod errors:', JSON.stringify(parsed.error.issues, null, 2));
        return { tasks: [], totalCount: 0, cacheStrategy: null, priorityThreshold: 50, filterApplied: null, generatedAt: null };
    }
    return parsed.data;
};

export const useLoadPrioritizedTasks = (
    request: PrioritizedTasksRequest
): UseSuspenseQueryResult<PrioritizedTasksResponse> => {
    return useSuspenseQuery<PrioritizedTasksResponse>({
        queryKey: [PRIORITIZED_TASKS_QUERY_KEY, serializeRequestKey(request)],
        queryFn: () => loadPrioritizedTasks(request),
    });
};

// ---- Column Filters ----

const loadColumnFilters = async (request: ColumnFiltersRequest): Promise<ColumnFiltersResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/location-list-column-filters`, {
            json: request,
        })
        .json();
    const parsed = columnFiltersResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadColumnFilters: response did not match schema, using fallback', parsed.error);
        return { statuses: [] };
    }
    return parsed.data;
};

export const useLoadColumnFilters = (
    request: ColumnFiltersRequest,
    options?: { enabled?: boolean }
): UseSuspenseQueryResult<ColumnFiltersResponse> => {
    return useSuspenseQuery<ColumnFiltersResponse>({
        queryKey: [COLUMN_FILTERS_QUERY_KEY, serializeRequestKey(request)],
        queryFn: () => loadColumnFilters(request),
        ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
    });
};

// ---- Responsible Users ----

const searchResponsibleUsers = async (request: ResponsibleUsersRequest): Promise<ResponsibleUsersResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/search-responsible-users`, {
            json: request,
        })
        .json();
    const parsed = responsibleUsersResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('searchResponsibleUsers: response did not match schema, using fallback', parsed.error);
        return { users: [] };
    }
    return parsed.data;
};

const EMPTY_RESPONSIBLE_USERS: ResponsibleUsersResponse = { users: [] };

export const useSearchResponsibleUsers = (
    request: ResponsibleUsersRequest
): UseQueryResult<ResponsibleUsersResponse> & { data: ResponsibleUsersResponse } => {
    const enabled = request.locationKeys.length > 0;
    return useQuery<ResponsibleUsersResponse>(
        {
            queryKey: [RESPONSIBLE_USERS_QUERY_KEY, serializeRequestKey(request)],
            queryFn: () => searchResponsibleUsers(request),
            enabled,
            initialData: EMPTY_RESPONSIBLE_USERS,
        }
    ) as UseQueryResult<ResponsibleUsersResponse> & { data: ResponsibleUsersResponse };
};

// ---- Update Status Mutation ----

export type UpdateStatusRequest = {
    locationTaskIds: number[];
    statusId: number;
};

const updateLocationTaskStatus = async (request: UpdateStatusRequest): Promise<void> => {
    await createPacerApi()
        .post(`${API_ROOT}location-task/update-status`, {
            json: request,
        });
};

export const useUpdateLocationTaskStatus = (
    callback?: () => void
): UseMutationResult<void, Error, UpdateStatusRequest> => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, UpdateStatusRequest>({
        mutationFn: updateLocationTaskStatus,
        onSuccess: () => {
            callback?.();
            queryClient.invalidateQueries({ queryKey: [PRIORITIZED_TASKS_QUERY_KEY] });
        },
    });
};
